import { Navigate, RouteProps } from 'react-router-dom'
import React from 'react'
import { PERMISSIONS } from '@/constants/permission';

const SmtpSetup = React.lazy(() => import('@/components/pages/settings/smtp_setup'));
const EmailConfiguration = React.lazy(() => import('@/components/pages/settings/email_configuration'));
const EmployeeTypeList = React.lazy(() => import('@/components/pages/master/employee_type/list'));
const DeviceTypeList = React.lazy(() => import('@/components/pages/master/device_type/list'));

const Login = React.lazy(() => import('@/components/pages/login'));
const UserList = React.lazy(() => import('@/components/pages/user_management/list'));
const AddUser = React.lazy(() => import('@/components/pages/user_management/add'));
const EditUser = React.lazy(() => import('@/components/pages/user_management/edit'));

const CompanyList = React.lazy(() => import('@/components/pages/company_management/list'));
const AddCompany = React.lazy(() => import('@/components/pages/company_management/add'));
const EditCompany = React.lazy(() => import('@/components/pages/company_management/edit'));
const CompanyStats = React.lazy(() => import('@/components/pages/company_management/stats'));

const EmployeeList = React.lazy(() => import('@/components/pages/employee_management/list'));
const AddEmployee = React.lazy(() => import('@/components/pages/employee_management/add'));
const EditEmployee = React.lazy(() => import('@/components/pages/employee_management/edit'));

const VendorList = React.lazy(() => import('@/components/pages/vendor_management/list'));
const AddVendor = React.lazy(() => import('@/components/pages/vendor_management/add'));
const EditVendor = React.lazy(() => import('@/components/pages/vendor_management/edit'));

const ServiceList = React.lazy(() => import('@/components/pages/services/list'));
const AddService = React.lazy(() => import('@/components/pages/services/add'));
const EditService = React.lazy(() => import('@/components/pages/services/edit'));

const EstimatedRateList = React.lazy(() => import('@/components/pages/estimated_rates/list'));
const EditEstimatedRate = React.lazy(() => import('@/components/pages/estimated_rates/edit'));

const FixedChargesList = React.lazy(() => import('@/components/pages/fixed_charges/list'));
const AddFixedCharges = React.lazy(() => import('@/components/pages/fixed_charges/add'));
const EditFixedCharge = React.lazy(() => import('@/components/pages/fixed_charges/edit'));

const InvoiceList = React.lazy(() => import('@/components/pages/invoice/list'));
const AddInvoice = React.lazy(() => import('@/components/pages/invoice/add'));
const EditInvoice = React.lazy(() => import('@/components/pages/invoice/edit'));

const PaymentList = React.lazy(() => import('@/components/pages/payments').then(module => ({ default: module.PaymentList })));
const AddPayment = React.lazy(() => import('@/components/pages/payments').then(module => ({ default: module.AddPayment })));

const CompanywiseCostingReport = React.lazy(() => import('@/components/pages/report').then(module => ({ default: module.CompanywiseCostingReport })));
const LicenceBasedCostingReport = React.lazy(() => import('@/components/pages/report').then(module => ({ default: module.LicenceBasedCostingReport })));

const LicenceList = React.lazy(() => import('@/components/pages/master/licences').then(module => ({ default: module.LicenceList })));

const ServiceCategoryList = React.lazy(() => import('@/components/pages/master/service_category').then(module => ({ default: module.ServiceCategoryList })));

const LicenceToCompanyReport = React.lazy(() => import('@/components/pages/report/license_to_company'));
const BudgetReport = React.lazy(() => import('@/components/pages/report/budget'));

const Profile = React.lazy(() => import('@/components/pages/profile'));
const ForgotPassword = React.lazy(() => import('@/components/pages/forgot_password'));
const ResetPassword = React.lazy(() => import('@/components/pages/reset_password'));
const ChangePassword = React.lazy(() => import('@/components/pages/change_password'));

export interface RoutesProps {
  path: RouteProps['path']
  name?: string
  element?: RouteProps['element']
  route?: any
  exact?: boolean
  icon?: string
  header?: string
  roles?: string[]
  children?: RoutesProps[],
  permission?: string,
}

// flatten the list of all nested routes
const flattenRoutes = (routes: RoutesProps[]) => {
  let flatRoutes: RoutesProps[] = []

  routes = routes || []
  routes.forEach((item: RoutesProps) => {
    flatRoutes.push(item)
    if (typeof item.children !== 'undefined') {
      flatRoutes = [...flatRoutes, ...flattenRoutes(item.children)]
    }
  })
  return flatRoutes
}

const mainRoutes: RoutesProps[] = [{
  path: '/',
  element: <Navigate to='/company-management' replace />,
  permission: PERMISSIONS['Company management'],
}, {
  path: '/user',
  name: 'User',
  header: 'Navigation',
  element: <UserList />,
  permission: PERMISSIONS['User Management'],
  children: [{
    path: '/user/add',
    name: 'Edit staff',
    header: 'Navigation',
    element: <AddUser />,
    permission: PERMISSIONS['User Management'],
  }, {
    path: '/user/:userId',
    name: 'Edit user',
    header: 'Navigation',
    element: <EditUser />,
    permission: PERMISSIONS['User Management'],
  }]
}, {
  path: '/company-management',
  name: 'Company Management',
  header: 'Navigation',
  element: <CompanyList />,
  permission: PERMISSIONS['Company management'],
  children: [{
    path: '/company-management/add',
    name: 'Add company',
    header: 'Navigation',
    element: <AddCompany />,
    permission: PERMISSIONS['Company management'],
  }, {
    path: '/company-management/:companyId',
    name: 'Add company',
    header: 'Navigation',
    element: <EditCompany />,
    permission: PERMISSIONS['Company management'],
  }, {
    path: '/company-management/stats/:companyId',
    name: 'Add company',
    header: 'Navigation',
    element: <CompanyStats />,
    permission: PERMISSIONS['Company management'],
  }]
},  {
  path: '/employee-management',
  name: 'Employee Management',
  header: 'Navigation',
  element: <EmployeeList />,
  permission: PERMISSIONS['Employee Management'],
  children: [{
    path: '/employee-management/add',
    name: 'User Details',
    header: 'Navigation',
    element: <AddEmployee />,
    permission: PERMISSIONS['Employee Management'],
  }, {
    path: '/employee-management/:employeeId',
    name: 'User Details',
    header: 'Navigation',
    element: <EditEmployee />,
    permission: PERMISSIONS['Employee Management'],
  }],
}, {
  path: '/vendor-management',
  name: 'Vendor Management',
  header: 'Navigation',
  element: <VendorList />,
  permission: PERMISSIONS['Vendor Management'],
  children: [{
    path: '/vendor-management/add',
    name: 'Add Vendor',
    header: 'Navigation',
    element: <AddVendor />,
    permission: PERMISSIONS['Vendor Management'],
  }, {
    path: '/vendor-management/:vendorId',
    name: 'Vendor Management Edit',
    header: 'Navigation',
    element: <EditVendor />,
    permission: PERMISSIONS['Vendor Management'],
  },]
}, {
  path: 'services',
  name: 'Services',
  header: 'Navigation',
  element: <ServiceList />,
  permission: PERMISSIONS.Services,
  children: [{
    path: '/services/add',
    name: 'Add Service',
    header: 'Navigation',
    element: <AddService />,
    permission: PERMISSIONS.Services,
  }, {
    path: '/services/:serviceId',
    name: 'Edit Service',
    header: 'Navigation',
    element: <EditService />,
    permission: PERMISSIONS.Services,
  }]
}, {
  path: '/estimated-rates',
  name: 'Estimated rate',
  header: 'Navigation',
  element: <EstimatedRateList />,
  permission: PERMISSIONS['Estimated Rates'],
  children: [{
    path: '/estimated-rates/:estimatedRateId',
    name: 'Rate details',
    header: 'Navigation',
    element: <EditEstimatedRate />,
    permission: PERMISSIONS['Estimated Rates'],
  }]
}, {
  path: '/fixed-charges',
  name: 'Fixed Charges',
  header: 'Navigation',
  element: <FixedChargesList />,
  permission: PERMISSIONS['Fixed Charges'],
  children: [{
    path: '/fixed-charges/add',
    name: 'Add Fixed Charges',
    header: 'Navigation',
    element: <AddFixedCharges />,
    permission: PERMISSIONS['Fixed Charges'],
  }, {
    path: '/fixed-charges/:fixedChargeId',
    name: 'Add Fixed Charges',
    header: 'Navigation',
    element: <EditFixedCharge />,
    permission: PERMISSIONS['Fixed Charges'],
  }]
}, {
  path: '/invoice',
  name: 'Invoice',
  header: 'Navigation',
  element: <InvoiceList />,
  permission: PERMISSIONS.Invoice,
  children: [{
    path: '/invoice/add',
    name: 'Invoice Details',
    header: 'Navigation',
    element: <AddInvoice />,
    permission: PERMISSIONS.Invoice,
  }, {
    path: '/invoice/:invoiceId',
    name: 'Invoice Details',
    header: 'Navigation',
    element: <EditInvoice />,
    permission: PERMISSIONS.Invoice,
  }]
}, {
  path: '/payments',
  name: 'Payments',
  header: 'Navigation',
  element: <PaymentList />,
  children: [{
    path: '/payments/:id',
    name: 'AddPayment',
    header: 'Navigation',
    element: <AddPayment />,
  }]
}, {
  path: '/report',
  name: 'Report',
  header: 'Navigation',
  permission: PERMISSIONS.Report,
  // element: <LicenceBasedCostingReport />,
  children: [{
    path: '/report/licence-based-costing',
    name: 'Report',
    header: 'Navigation',
    element: <LicenceBasedCostingReport />,
    permission: PERMISSIONS.Report,
  }, {
    path: '/report/companywise-costing',
    name: 'Report',
    header: 'Navigation',
    element: <CompanywiseCostingReport />,
    permission: PERMISSIONS.Report,
  }]
}, {
  path: 'settings',
  name: 'Settings',
  header: 'Settings',
  permission: PERMISSIONS['SMTP Setup'],
  children: [
    {
      path: '/smtp-setup',
      name: 'SMTP Setup',
      element: <SmtpSetup />,
      permission: PERMISSIONS['SMTP Setup'],
    },
    {
      path: '/settings/email-configuration',
      name: 'Email Configuration',
      element: <EmailConfiguration />,
      permission: PERMISSIONS['SMTP Setup'],
    },
  ],
}, {
  path: '/licences',
  name: 'Licences',
  header: 'Navigation',
  element: <LicenceList />,
  permission: PERMISSIONS.Licences,
}, {
  path: '/service-category',
  name: 'Service Category',
  header: 'Navigation',
  element: <ServiceCategoryList />,
  permission: PERMISSIONS['Service Category'],
}, {
  path: '/employee-type',
  name: 'Licences',
  header: 'Navigation',
  element: <EmployeeTypeList />,
  permission: PERMISSIONS['Employee Type'],
}, {
  path: '/device-type',
  name: 'Licences',
  header: 'Navigation',
  element: <DeviceTypeList />,
  permission: PERMISSIONS['Device Type'],
}, {
  path: '/license-to-company-report',
  name: 'Licence to company',
  header: 'Navigation',
  element: <LicenceToCompanyReport />,
  permission: PERMISSIONS['Device Type'],
}, {
  path: '/budget-report',
  name: 'Budget',
  header: 'Navigation',
  element: <BudgetReport />,
  permission: PERMISSIONS['Device Type'],
}, {
  path: '/profile',
  name: 'Profile',
  header: 'Navigation',
  element: <Profile />,
  permission: PERMISSIONS.Profile,
}, {
  path: '/change-password',
  name: 'change password',
  header: 'Navigation',
  element: <ChangePassword />,
  permission: PERMISSIONS['Change Password'],
}
];

const publicRoutes = [{
  path: '/login',
  element: <Login />
}, {
  path: '/forgot-password',
  name: 'Forgot password',
  header: 'Navigation',
  element: <ForgotPassword />,
}, {
  path: '/reset',
  name: 'Reset password',
  header: 'Navigation',
  element: <ResetPassword />,
}]

const authProtectedRoutes: any = []

const authProtectedFlattenRoutes = flattenRoutes(mainRoutes)

export {
  authProtectedFlattenRoutes,
  authProtectedRoutes,
  publicRoutes,
  mainRoutes,
};
